import { api, ApiError } from '../../lib/api';
import type { FranchiseProfile, FranchiseProfileInput } from '../../types/franchiseProfile';
import type {
  FranchiseLocationProfile,
  FranchiseLocationProfileInput,
} from '../../types/franchiseLocationProfile';

async function maybe<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** What the voice-profile drafter proposes. ⚠️ A PROPOSAL — nothing is saved. */
export type VoiceProfileDraft = {
  profile: string;
  /** How much past copy it read, so thin evidence is visible on screen. */
  sampledAds: number;
  sampledCampaigns: number;
  /** Whether the brand's ad-copy doc contributed, and why not if it didn't. */
  referenceDoc: { read: boolean; reason: string | null };
  model: string;
};

export async function getFranchiseProfile(accountId: string): Promise<FranchiseProfile | null> {
  return maybe(async () => {
    const res = await api<{ profile: FranchiseProfile }>(`/api/franchise/profiles/${accountId}`);
    return res.profile;
  });
}

export async function createFranchiseProfile(
  accountId: string,
  input: Omit<FranchiseProfileInput, 'accountId'>,
): Promise<FranchiseProfile> {
  const res = await api<{ profile: FranchiseProfile }>(
    `/api/franchise/profiles/${accountId}`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return res.profile;
}

export async function updateFranchiseProfile(
  accountId: string,
  patch: Partial<Omit<FranchiseProfileInput, 'accountId'>>,
): Promise<FranchiseProfile> {
  const res = await api<{ profile: FranchiseProfile }>(
    `/api/franchise/profiles/${accountId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.profile;
}

// OrganicAssets spec §6.4. Validate + set (or clear) the brand's Drive
// Rollout Parent Folder. The server parses the pasted link/ID, checks the
// service account has Editor on it, then persists. Returns the resolved
// folder name (null when cleared) so the UI can confirm the binding.
export async function configureBrandDriveParent(
  accountId: string,
  folderInput: string | null,
): Promise<{ profile: FranchiseProfile; folderName: string | null }> {
  return api<{ profile: FranchiseProfile; folderName: string | null }>(
    `/api/franchise/profiles/${accountId}/drive-parent`,
    { method: 'POST', body: JSON.stringify({ folderInput }) },
  );
}

export async function getFranchiseLocationProfile(
  accountId: string,
): Promise<FranchiseLocationProfile | null> {
  return maybe(async () => {
    const res = await api<{ profile: FranchiseLocationProfile }>(
      `/api/franchise/locationProfiles/${accountId}`,
    );
    return res.profile;
  });
}

export async function createFranchiseLocationProfile(
  accountId: string,
  input: Omit<FranchiseLocationProfileInput, 'accountId'>,
): Promise<FranchiseLocationProfile> {
  const res = await api<{ profile: FranchiseLocationProfile }>(
    `/api/franchise/locationProfiles/${accountId}`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return res.profile;
}

export async function updateFranchiseLocationProfile(
  accountId: string,
  patch: Partial<Omit<FranchiseLocationProfileInput, 'accountId'>>,
): Promise<FranchiseLocationProfile> {
  const res = await api<{ profile: FranchiseLocationProfile }>(
    `/api/franchise/locationProfiles/${accountId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.profile;
}

/**
 * Propose a voice profile from this brand's past campaign copy.
 *
 * ⚠️⚠️ IT PROPOSES, IT NEVER SAVES. The draft comes back for the AM to edit; the
 * ordinary profile save is what stores it, and that human edit is the real
 * filter — increasingly so, because once campaign copy is AI-written the past
 * copy this reads increasingly IS AI output.
 *
 * ⚠️ ON-DEMAND ONLY: it reads a brand's whole campaign history and calls Opus.
 */
export async function draftBrandVoiceProfile(
  accountId: string,
): Promise<VoiceProfileDraft> {
  return api<VoiceProfileDraft>(
    `/api/franchise/profiles/${accountId}/draft-voice`,
    { method: 'POST' },
  );
}
